import * as THREE from 'three';
import type { WorldState, TransitionDirection } from '@/types';
import {
  EXT_CAMERA_POS, EXT_CAMERA_LOOKAT, EXT_CAMERA_FOV, EXT_CAMERA_NEAR, EXT_CAMERA_FAR,
  INT_CAMERA_POS, INT_CAMERA_LOOKAT, INT_CAMERA_FOV,
} from '@/utils/constants';

const FADE_DURATION = 0.5; // seconds for fade in or out

/**
 * World state machine: LOADING → HOMEPAGE → EXTERIOR → TRANSITIONING → INTERIOR
 * Transition: fade to black → swap scene → fade from black (no dolly, no double-fade).
 * Opacity is driven directly via JS each frame — no CSS transitions involved.
 */
export class SceneManager {
  currentState: WorldState = 'LOADING';

  private homepageScene: THREE.Scene | null = null;
  private homepageCamera: THREE.OrthographicCamera | null = null;
  private renderingHomepage = false;

  private exteriorScene: THREE.Scene | null = null;
  private interiorScene: THREE.Scene | null = null;

  private exteriorCamera: THREE.PerspectiveCamera;
  private interiorCamera: THREE.PerspectiveCamera;

  // Which scene/camera is currently being rendered (swapped while fully black)
  private renderingInterior = false;

  // Transition
  private transitionTimer = 0;
  private transitionPhase: 'fade-in' | 'fade-out' | null = null;
  private transitionDirection: TransitionDirection = 'enter';
  private overlay: HTMLElement | null = null;
  private swapped = false;

  private onInteriorNeeded: (() => Promise<void>) | null = null;
  private onStateChange: ((state: WorldState) => void) | null = null;
  private interiorReady = false;

  constructor() {
    const aspect = window.innerWidth / window.innerHeight;

    this.exteriorCamera = new THREE.PerspectiveCamera(EXT_CAMERA_FOV, aspect, EXT_CAMERA_NEAR, EXT_CAMERA_FAR);
    this.exteriorCamera.position.copy(EXT_CAMERA_POS);
    this.exteriorCamera.lookAt(EXT_CAMERA_LOOKAT);

    this.interiorCamera = new THREE.PerspectiveCamera(INT_CAMERA_FOV, aspect, 0.1, 100);
    this.interiorCamera.position.copy(INT_CAMERA_POS);
    this.interiorCamera.lookAt(INT_CAMERA_LOOKAT);

    this.overlay = document.getElementById('fade-overlay');
    // Remove CSS transition — we'll drive opacity manually
    if (this.overlay) {
      this.overlay.style.transition = 'none';
      this.overlay.style.opacity = '0';
      this.overlay.style.pointerEvents = 'none';
    }
  }

  // ── Scene setters ──────────────────────────────────────────
  setHomepageScene(scene: THREE.Scene, camera: THREE.OrthographicCamera): void {
    this.homepageScene = scene;
    this.homepageCamera = camera;
  }

  setExteriorScene(scene: THREE.Scene): void { this.exteriorScene = scene; }
  setInteriorScene(scene: THREE.Scene): void { this.interiorScene = scene; this.interiorReady = true; }
  setOnInteriorNeeded(cb: () => Promise<void>): void { this.onInteriorNeeded = cb; }
  setOnStateChange(cb: (state: WorldState) => void): void { this.onStateChange = cb; }

  // ── Camera / scene getters ────────────────────────────────
  getActiveCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    if (this.renderingHomepage && this.homepageCamera) return this.homepageCamera;
    return this.renderingInterior ? this.interiorCamera : this.exteriorCamera;
  }

  getActiveScene(): THREE.Scene | null {
    if (this.renderingHomepage) return this.homepageScene;
    return this.renderingInterior ? this.interiorScene : this.exteriorScene;
  }

  getExteriorCamera(): THREE.PerspectiveCamera { return this.exteriorCamera; }
  getInteriorCamera(): THREE.PerspectiveCamera { return this.interiorCamera; }

  // ── State entry points ────────────────────────────────────
  enterHomepage(): void {
    this.currentState = 'HOMEPAGE';
    this.renderingHomepage = true;
    this.renderingInterior = false;
    this.onStateChange?.('HOMEPAGE');
  }

  enterExterior(): void {
    this.currentState = 'EXTERIOR';
    this.renderingHomepage = false;
    this.renderingInterior = false;
    this.onStateChange?.('EXTERIOR');
  }

  // ── Transition system ─────────────────────────────────────
  startTransition(direction: TransitionDirection): void {
    if (this.currentState === 'TRANSITIONING') return;

    this.transitionDirection = direction;
    this.currentState = 'TRANSITIONING';
    this.transitionTimer = 0;
    this.transitionPhase = 'fade-in';
    this.swapped = false;

    window.dispatchEvent(new CustomEvent('transition-start', { detail: { direction } }));
  }

  update(delta: number): void {
    if (this.currentState !== 'TRANSITIONING') return;

    this.transitionTimer += delta;

    if (this.transitionPhase === 'fade-in') {
      const t = Math.min(this.transitionTimer / FADE_DURATION, 1);
      if (this.overlay) {
        this.overlay.style.opacity = String(t);
        this.overlay.style.pointerEvents = 'auto';
      }

      if (t >= 1) {
        // Fully black — do the swap
        if (!this.swapped) {
          this.swapped = true;

          if (this.transitionDirection === 'homepage-enter') {
            // Homepage → Interior (skip exterior)
            this.renderingHomepage = false;
            if (!this.interiorReady && this.onInteriorNeeded) {
              this.onInteriorNeeded().then(() => {
                this.interiorReady = true;
                this.renderingInterior = true;
                this.beginFadeOut();
              });
              return; // Wait for async load
            }
            this.renderingInterior = true;
            this.beginFadeOut();
          } else if (this.transitionDirection === 'enter') {
            if (!this.interiorReady && this.onInteriorNeeded) {
              this.onInteriorNeeded().then(() => {
                this.interiorReady = true;
                this.renderingInterior = true;
                this.beginFadeOut();
              });
              return; // Wait for async load
            }
            this.renderingInterior = true;
            this.beginFadeOut();
          } else {
            // exit: interior → exterior
            this.renderingInterior = false;
            // Reset exterior camera
            this.exteriorCamera.position.copy(EXT_CAMERA_POS);
            this.exteriorCamera.lookAt(EXT_CAMERA_LOOKAT);
            this.beginFadeOut();
          }
        }
      }
    } else if (this.transitionPhase === 'fade-out') {
      const t = Math.min(this.transitionTimer / FADE_DURATION, 1);
      if (this.overlay) {
        this.overlay.style.opacity = String(1 - t);
      }

      if (t >= 1) {
        if (this.overlay) {
          this.overlay.style.opacity = '0';
          this.overlay.style.pointerEvents = 'none';
        }
        this.transitionPhase = null;

        let newState: WorldState;
        if (this.transitionDirection === 'enter' || this.transitionDirection === 'homepage-enter') {
          newState = 'INTERIOR';
        } else {
          newState = 'EXTERIOR';
        }

        this.currentState = newState;
        this.onStateChange?.(newState);
        window.dispatchEvent(new CustomEvent('transition-end', { detail: { state: newState } }));
      }
    }
  }

  private beginFadeOut(): void {
    this.transitionPhase = 'fade-out';
    this.transitionTimer = 0;
  }

  handleResize(): void {
    const aspect = window.innerWidth / window.innerHeight;
    this.exteriorCamera.aspect = aspect;
    this.exteriorCamera.updateProjectionMatrix();
    this.interiorCamera.aspect = aspect;
    this.interiorCamera.updateProjectionMatrix();

    // Homepage OrthographicCamera resize is handled by homepage-scene.ts resizeHomepage()
  }
}
